import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { BaseService } from 'src/app/helpers/base.service';
import {
  iEmpRoleRequest,
  iEmpRoleResult,
} from 'src/app/helpers/Interfaces/emp-roles';
import { iPaginationRequest, iPaginationResult } from 'src/app/helpers/Interfaces/pagination';
import {
  iPermissionModuleRequest,
  iPermissionModuleResult,
} from 'src/app/helpers/Interfaces/permission-modules';
import {
  iPermissionsRequest,
  iPermissionsResult,
} from 'src/app/helpers/Interfaces/permissions';
import { RightsResponse } from 'src/app/helpers/Interfaces/rights';
import { Nullable } from 'src/app/helpers/Types/types';
import { environment } from 'src/environments/environment.development';

@Injectable({
  providedIn: 'root',
})
export class RolesService extends BaseService {
  private readonly _permissionModule = `${environment.base_url}/api/PermissionModule`;
  private readonly _empRole = `${environment.base_url}/api/EmpRole`;
  private readonly _permissions = `${environment.base_url}/api/Permissions`;

  private readonly _getPermissionModule = `${this._permissionModule}/GetAll`;
  private readonly _permissionModuleAddRange = `${this._permissionModule}/AddRange`;

  private _rightsGetAll = `${environment.base_url}/api/Rights/GetAll`;

  private readonly _getEmpRole = `${this._empRole}/GetAll`;
  private readonly _getAllEmpRolesWithRelationShip = `${this._empRole}/GetAllNew`;
  private readonly _getEmpRoleById = `${this._empRole}/GetById`;
  private readonly _addEmpRole = `${this._empRole}/Add`;
  private readonly _addEmpRoleRange = `${this._empRole}/AddRange`;
  private readonly _updateEmpRole = `${this._empRole}/Update`;
  private readonly _deleteEmpRole = `${this._empRole}/Remove`;

  private readonly _getAllPermissions = `${this._permissions}/GetAll`;
  private readonly _getCodesByEmpRoleId = `${this._permissions}/GetCodesByEmpRoleId`;
  // private readonly _getPermissions = `${this._permissions}/Get`;
  private readonly _getPermissionsByEmployee = `${this._permissions}/GetByEmployeeId`;
  private readonly _getPermissionsByEmpRole = `${this._permissions}/GetByEmpRoleId`;
  private readonly _addPermissions = `${this._permissions}/Add`;
  private readonly _updatePermissions = `${this._permissions}/Update`;

  private readonly _permanentDelete = `${this._empRole}/RemoveRange`;

  constructor() {
    super();
  }

  permissionModuleAddRange(payload: iPermissionModuleRequest[]) {
    return this._http.post<iPermissionModuleResult[]>(
      this._permissionModuleAddRange,
      payload,
      {
        responseType: 'json',
      }
    );
  }
  getPermissionModule() {
    return this._http
      .get<iPermissionModuleResult[]>(this._getPermissionModule, {
        responseType: 'json',
      })
      .pipe(
        map((res) => {
          res.forEach((x) => {
            x.rights = x.rights.filter((x) => x.active == 'Y');
            return x;
          });
          res = res.filter((x) => x.active == 'Y');
          return res;
        })
      );
  }

  rightsGetAll() {
    return this._http
      .get<RightsResponse[]>(this._rightsGetAll, {
        responseType: 'json',
      })
      .pipe(
        map((rights) => {
          rights = rights.filter((x) => !x.rightId);
          const sortRecursive: (
            rights: RightsResponse[]
          ) => RightsResponse[] = (rights: RightsResponse[]) => {
            return rights
              .sort((a, b) => a.name.localeCompare(b.name)) // Sort by name
              .map((item) => ({
                ...item,
                rightList: sortRecursive(item.rightList), // Recursively sort nested rights
              }));
          };

          return sortRecursive([...rights]);
        })
      );
  }

  getEmpRole() {
    return this._http
      .get<iEmpRoleResult[]>(this._getEmpRole, {
        responseType: 'json',
      })
      .pipe(map((res) => res.reverse()));
  }
  getAllWithRelationShip(payload: iPaginationRequest) {
    return this._http.post<iPaginationResult<iEmpRoleResult>>(
      `${this._getAllEmpRolesWithRelationShip}`,
      payload,
      {
        responseType: 'json',
      }
    );
  }
  getEmpRoleById(id: string) {
    return this._http.get<iEmpRoleResult>(`${this._getEmpRoleById}?id=${id}`, {
      responseType: 'json',
    });
  }
  addEmpRole(payload: iEmpRoleRequest) {
    return this._http.post<iEmpRoleResult>(this._addEmpRole, payload, {
      responseType: 'json',
    });
  }
  addEmpRoleRange(payload: iEmpRoleRequest[]) {
    return this._http.post<iEmpRoleResult[]>(this._addEmpRoleRange, payload, {
      responseType: 'json',
    });
  }
  updateEmpRole(payload: iEmpRoleRequest) {
    return this._http.put<iEmpRoleResult>(this._updateEmpRole, payload, {
      responseType: 'json',
    });
  }
  deleteEmpRole(id: string) {
    return this._http.delete(`${this._deleteEmpRole}?id=${id}`);
  }

  // private _processPermissions(
  //   permissions: Nullable<iPermissionsResult>
  // ): Nullable<iPermissionsDto> {
  //   return permissions
  //     ? {
  //         ...permissions,
  //         modules: [],
  //       }
  //     : null;
  // }
  getCodesByEmpRoleId(RoleId: string) {
    return this._http.get<string[]>(this._getCodesByEmpRoleId, {
      responseType: 'json',
      params: { RoleId },
    });
  }
  getAllPermissions() {
    return this._http.get<iPermissionsResult[]>(this._getAllPermissions, {
      responseType: 'json',
    });
  }
  getPermissionsByEmployee(employeeId: string) {
    return this._http.get<Nullable<iPermissionsResult>>(
      `${this._getPermissionsByEmployee}?employeeId=${employeeId}`,
      {
        responseType: 'json',
      }
    );
  }
  getPermissionsByEmpRole(empRoleId: string) {
    return this._http.get<iPermissionsResult | null>(
      `${this._getPermissionsByEmpRole}?EmpRoleId=${empRoleId}`,
      {
        responseType: 'json',
      }
    );
  }
  // getPermissions(empRoleId: string, employeeId: string) {
  //   return this._http
  //     .get<Nullable<iPermissionsResult[]>>(
  //       `${this._getPermissions}?EmpRoleId=${empRoleId}&EmployeeId=${employeeId}`,
  //       {
  //         responseType: 'json',
  //       }
  //     )
  //     .pipe(
  //       map((res) => (res || []).map(this._processPermissions)),
  //       map((res) => {
  //         const permissions: Array<iPermissionsModule> = [];

  //         res.forEach((x) => {
  //           if (x)
  //             x.modules.forEach((y) => {
  //               const existingModule = permissions.find(
  //                 (perm) => perm.id === y.id
  //               );

  //               if (existingModule) {
  //                 if (!y.isDefault) existingModule.canActivate = y.canActivate;

  //                 y.rights.forEach((right) => {
  //                   const existingRight = existingModule.rights.find(
  //                     (r) => r.id === right.id
  //                   );
  //                   if (!right.isDefault)
  //                     if (existingRight) {
  //                       existingRight.canActivate = right.canActivate;
  //                     } else {
  //                       existingModule.rights.push(right);
  //                     }
  //                 });
  //               } else {
  //                 permissions.push({ ...y, rights: [...y.rights] });
  //               }
  //             });
  //         });

  //         return permissions;
  //       })
  //     );
  // }
  // getPermissionsAlt(empRoleId: string, employeeId: string) {
  //   return this._http
  //     .get<Nullable<iPermissionsResult[]>>(
  //       `${this._getPermissions}?EmpRoleId=${empRoleId}&EmployeeId=${employeeId}`,
  //       {
  //         responseType: 'json',
  //       }
  //     )
  //     .pipe(
  //       map((res) => (res || []).map(this._processPermissions)),
  //       map((res) => {
  //         const permissions: Array<iPermissionsModule> = [];
  //         const returnDto: iPermissionsDto = {
  //           id: '',
  //           empRoleId: '',
  //           employeeId: '',
  //           modules: [],
  //           name: undefined,
  //           status: undefined,
  //           rights: [],
  //         };

  //         if (res[1]) {
  //           returnDto.id = res[1].id;
  //           returnDto.empRoleId = res[1].empRoleId;
  //           returnDto.employeeId = res[1].employeeId;
  //           returnDto.name = res[1].name;
  //           returnDto.status = res[1].status;
  //         }

  //         res.forEach((x) => {
  //           if (x)
  //             x.modules.forEach((y) => {
  //               const existingModule = permissions.find(
  //                 (perm) => perm.id === y.id
  //               );

  //               if (existingModule) {
  //                 if (!y.isDefault) {
  //                   existingModule.canActivate = y.canActivate;

  //                   y.rights.forEach((right) => {
  //                     const existingRight = existingModule.rights.find(
  //                       (r) => r.id === right.id
  //                     );

  //                     if (existingRight) {
  //                       existingRight.canActivate = right.canActivate;
  //                     } else {
  //                       existingModule.rights.push(right);
  //                     }
  //                   });
  //                 }
  //               } else {
  //                 permissions.push({ ...y, rights: [...y.rights] });
  //               }
  //             });
  //         });

  //         return {
  //           permissions,
  //           returnDto,
  //           rolePermissions: res[0],
  //           userPermissions: res[1],
  //         };
  //       })
  //     );
  // }

  addPermissions(payload: iPermissionsRequest) {
    return this._http.post<iPermissionsResult>(this._addPermissions, payload, {
      responseType: 'json',
    });
  }
  updatePermissions(payload: iPermissionsRequest) {
    return this._http.put<iPermissionsResult>(
      this._updatePermissions,
      payload,
      {
        responseType: 'json',
      }
    );
  }
  permanentDeleteRange(ids: string[]) {
    return this._http.post(`${this._permanentDelete}`, ids, {
      responseType: 'json',
    });
  }
}
